import { Box, Collapse, Paper, Stack, useTheme } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import StudyAndSeriesDefinitionForm from './StudyAndSeriesDefinitionForm'
import { QcTaskExamDescriptionValues } from '../../qc_task_details_view.types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  QcStudyDefinitionCreateFormValues,
  qcStudyDefinitionCreateSchema,
} from '../../schemas/StudyAndSeriesDefinitionValidationSchema'
import {
  createQcStudyDefinitionQcTasksQcTaskIdSubmissionsQcTaskSubmissionIdStudyDefinitionsPost,
  QcTimepointContainerSubmission,
  updateQcStudyDefinitionQcTasksQcTaskIdSubmissionsQcTaskSubmissionIdStudyDefinitionsStudyDefinitionIdPut,
} from '@common/config/api/client'
import { queryClient } from '@common/config/api/queryClient'
import { useParams } from 'react-router-dom'
import { SPACING } from '@common/theme/spacing'
import StudyAndSeriesDefinitionHeader from './StudyAndSeriesDefinitionHeader'
import StudyAndSeriesDefinitionInstance from './StudyAndSeriesDefinitionInstance'
import { useStudyAndSeriesFlush } from '../../contexts/StudyAndSeriesFlushContext'
import { toast } from 'sonner'

export default function StudyAndSeriesDefinition({
  examDescription,
  qcTaskSubmissionId,
  qcTimepointContainerSubmissions,
  removeStudy,
  editStudy,
  saveStudy,
  onDraftSaved,
  qcStatus,
  order,
}: {
  examDescription: QcTaskExamDescriptionValues
  qcTaskSubmissionId: string
  qcTimepointContainerSubmissions: QcTimepointContainerSubmission[]
  removeStudy: (id: string) => void
  editStudy: (id: string) => void
  saveStudy: (id: string) => void
  onDraftSaved: (tempId: string) => void
  qcStatus: string
  order: number
}) {
  const [open, setOpen] = useState(true)
  const { qcTaskId } = useParams()
  const theme = useTheme()
  const { registerFlush, unregisterFlush } = useStudyAndSeriesFlush()

  const isEditable = !(examDescription.isSubmitted && !examDescription.isEditing)

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<QcStudyDefinitionCreateFormValues>({
    resolver: zodResolver(qcStudyDefinitionCreateSchema),
    mode: 'onSubmit',
    defaultValues: {
      study_description: examDescription.study_description || '',
      exam_metadata_ids: examDescription.source_study_description.map(
        (sourceStudyDescription) => sourceStudyDescription.container_submission_exam_metadata_id,
      ),
      series_definitions:
        examDescription.series_descriptions.map((seriesDescription, index) => ({
          order: index + 1,
          is_selected: false,
          series_label_id: seriesDescription.series_label_id || '',
          series_label_index: seriesDescription.series_label_index ?? null,
          submission_series_id: seriesDescription.submission_series_id,
          series_metadata_id: seriesDescription.source_series_description.container_submission_series_metadata_id,
          instance_number_ranges: seriesDescription.instance_number_ranges.map((instanceNumberRange) => ({
            start: instanceNumberRange.start || 1,
            end: instanceNumberRange.end || 1,
          })),
        })) || [],
    },
  })

  const saveDefinition = useCallback(
    async (data: QcStudyDefinitionCreateFormValues) => {
      if (examDescription.isEditing) {
        await updateQcStudyDefinitionQcTasksQcTaskIdSubmissionsQcTaskSubmissionIdStudyDefinitionsStudyDefinitionIdPut({
          body: data,
          path: {
            qc_task_id: qcTaskId!,
            qc_task_submission_id: qcTaskSubmissionId,
            study_definition_id: examDescription.submission_exam_id,
          },
          throwOnError: true,
        })
        saveStudy(examDescription.submission_exam_id)
      } else {
        await createQcStudyDefinitionQcTasksQcTaskIdSubmissionsQcTaskSubmissionIdStudyDefinitionsPost({
          body: data,
          path: { qc_task_id: qcTaskId!, qc_task_submission_id: qcTaskSubmissionId },
          throwOnError: true,
        })
        onDraftSaved(examDescription.submission_exam_id)
      }
      reset(getValues())
    },
    [
      examDescription.isEditing,
      examDescription.submission_exam_id,
      getValues,
      onDraftSaved,
      qcTaskId,
      qcTaskSubmissionId,
      reset,
      saveStudy,
    ],
  )

  const onSubmit = async (data: QcStudyDefinitionCreateFormValues) => {
    try {
      await saveDefinition(data)
      await queryClient.invalidateQueries({ queryKey: ['QCTask'] })
    } catch {
      toast.error('Failed to save study and series definition')
    }
  }

  const flush = useCallback(async () => {
    if (!isEditable) {
      return true
    }

    setOpen(true)

    const isValid = await trigger()
    if (!isValid) {
      return false
    }

    if (examDescription.isSubmitted && !isDirty) {
      return true
    }

    let data: QcStudyDefinitionCreateFormValues
    try {
      data = qcStudyDefinitionCreateSchema.parse(getValues())
    } catch {
      return false
    }

    try {
      await saveDefinition(data)
      return true
    } catch {
      toast.error('Failed to save study and series definition')
      return false
    }
  }, [examDescription.isSubmitted, getValues, isDirty, isEditable, saveDefinition, trigger])

  useEffect(() => {
    registerFlush(examDescription.submission_exam_id, flush)
    return () => unregisterFlush(examDescription.submission_exam_id)
  }, [examDescription.submission_exam_id, flush, registerFlush, unregisterFlush])

  return (
    <Paper
      sx={{
        padding: SPACING.spacingLg,
        borderRadius: SPACING.borderRadiusXxl,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box component={isEditable ? 'form' : 'div'} onSubmit={isEditable ? handleSubmit(onSubmit) : undefined}>
        <Stack spacing={SPACING.spacingXl}>
          <StudyAndSeriesDefinitionHeader
            open={open}
            setOpen={setOpen}
            study={examDescription}
            removeStudy={removeStudy}
            editStudy={editStudy}
            saveDisabled={isSubmitting}
            qcStatus={qcStatus}
            order={order}
          />
          <Collapse in={open} timeout="auto">
            {examDescription.isSubmitted && !examDescription.isEditing ? (
              <StudyAndSeriesDefinitionInstance
                qcStatus={qcStatus}
                order={order}
                qcTaskExamDescription={examDescription}
              />
            ) : (
              <StudyAndSeriesDefinitionForm
                control={control}
                saveDisabled={isSubmitting}
                qcTimepointContainerSubmissions={qcTimepointContainerSubmissions}
              />
            )}
          </Collapse>
        </Stack>
      </Box>
    </Paper>
  )
}
